import type { ElementType, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Card.module.css'

export type CardTone = 'light' | 'plain' | 'dark' | 'deep' | 'accent'

export interface CardProps {
  readonly as?: ElementType | undefined
  readonly tone?: CardTone | undefined
  readonly padded?: boolean | undefined
  readonly interactive?: boolean | undefined
  readonly className?: string | undefined
  readonly children: ReactNode
  readonly onClick?: (() => void) | undefined
  readonly to?: string | undefined
}

/**
 * Compound card. Children compose freely — the card owns surface, radius and
 * padding, nothing else. Card.Header / Card.Body / Card.Footer are optional.
 */
export function Card({
  as,
  tone = 'light',
  padded = true,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps) {
  const Tag: ElementType = as ?? (interactive ? 'button' : 'div')
  return (
    <Tag
      className={cn(
        styles.card,
        styles[tone],
        padded && styles.padded,
        interactive && styles.interactive,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  )
}

Card.Header = function CardHeader({
  className,
  children,
}: {
  readonly className?: string | undefined
  readonly children: ReactNode
}) {
  return <div className={cn(styles.header, className)}>{children}</div>
}

Card.Body = function CardBody({
  className,
  children,
}: {
  readonly className?: string | undefined
  readonly children: ReactNode
}) {
  return <div className={cn(styles.body, className)}>{children}</div>
}

Card.Footer = function CardFooter({
  className,
  children,
}: {
  readonly className?: string | undefined
  readonly children: ReactNode
}) {
  return <div className={cn(styles.footer, className)}>{children}</div>
}
